"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import FaqAccordion from "@components/FaqAccordion";
import { track } from "@lib/track";
import { feeLabel } from "@lib/fees";

type Path = "discovery" | "consultation" | "farleigh" | "bodhi";

const QUESTIONS = [
  {
    text: "What's your biggest health goal right now?",
    options: [
      { label: "Boost energy, sleep or mood", scores: { farleigh: 1, bodhi: 1 } },
      { label: "Address weight, digestion or hormones", scores: { consultation: 1, farleigh: 1 } },
      { label: "Understand a chronic or long-standing condition", scores: { consultation: 2 } },
      { label: "Not sure yet, I just want to feel better", scores: { discovery: 2 } },
    ],
  },
  {
    text: "What kind of support feels right?",
    options: [
      { label: "A personal deep-dive with a specialist", scores: { consultation: 2 } },
      { label: "A structured, step-by-step programme", scores: { farleigh: 2 } },
      { label: "Monthly expert guidance and community", scores: { bodhi: 2 } },
      { label: "A friendly, no-pressure chat first", scores: { discovery: 2 } },
    ],
  },
  {
    text: "How ready are you to invest in your health?",
    options: [
      { label: "Just exploring for now", scores: { discovery: 2 } },
      { label: "Open to a manageable monthly commitment", scores: { bodhi: 2 } },
      { label: "Ready to make a meaningful investment", scores: { consultation: 1, farleigh: 1 } },
      { label: "Whatever it takes for real, lasting results", scores: { consultation: 2 } },
    ],
  },
];

const RESULTS: Record<
  Path,
  { emoji: string; title: string; tagline: string; cta: string; href: string; charge: string; highlights: string[]; badge?: string }
> = {
  discovery: {
    emoji: "☕",
    title: "Start with a Free Discovery Call",
    tagline: "A calm 15-minute chat with Niki to work out where to start.",
    cta: "Book My Free Call",
    href: "/workwithme/discoverycall",
    charge: "Free",
    highlights: ["15 minutes, no obligation", "Understand all your options", "Led personally by Niki"],
  },
  farleigh: {
    emoji: "🧭",
    title: "The Farleigh Method",
    tagline: "A structured protocol that tracks what changes and adapts as you go.",
    cta: "Explore the Farleigh Method",
    href: "/workwithme/farleighMethod",
    charge: `From ${feeLabel("farleighMethod")}`,
    highlights: ["Systematic: one change at a time", "Root-cause focused", "Clear, measurable milestones"],
    badge: "Signature Programme",
  },
  consultation: {
    emoji: "✨",
    title: "Personalised Consultation",
    tagline: "A full 60-minute deep-dive to find root causes and build a real plan.",
    cta: "Book a Consultation",
    href: "/workwithme/consultation",
    charge: `From ${feeLabel("consultation")}`,
    highlights: ["60-min 1-to-1 with Niki", "Full health history review", "Personalised nutrition plan"],
  },
  bodhi: {
    emoji: "🌿",
    title: "Bodhi Health Club",
    tagline: `Monthly expert guidance, community and accountability, from ${feeLabel("bodhiClub")}.`,
    cta: "Join the Bodhi Club",
    href: "/workwithme/align",
    charge: `${feeLabel("bodhiClub")}/month`,
    highlights: ["Monthly 1-to-1 with Niki", "Group coaching sessions", "Cancel anytime"],
  },
};

const GOALS = [
  { icon: "⚡", label: "Energy & fatigue",     href: "/workwithme/discoverycall" },
  { icon: "🌙", label: "Sleep & stress",        href: "/workwithme/discoverycall" },
  { icon: "🫶", label: "Gut & digestion",       href: "/workwithme/consultation" },
  { icon: "⚖️", label: "Weight & hormones",     href: "/workwithme/consultation" },
  { icon: "🧠", label: "Brain fog & mood",      href: "/workwithme/discoverycall" },
  { icon: "🤱", label: "Fertility & pregnancy", href: "/workwithme/consultation" },
  { icon: "👶", label: "Children's health",     href: "/workwithme/consultation" },
  { icon: "❤️", label: "Chronic conditions",    href: "/workwithme/consultation" },
];

const TESTIMONIALS = [
  {
    quote: "I've never felt so well, so grounded, and so in tune with myself.",
    body: "Nichola helped me detox and reclaim my health. I feel emotionally and spiritually stronger than ever.",
    name: "Lucie W.",
    role: "Wellness Programme",
  },
  {
    quote: "Niki's passion and enthusiasm is infectious.",
    body: "Her patience in answering any questions is endless. Everything we learned was accessible and simple to continue at home.",
    name: "Jayne G.",
    role: "Retreat Guest",
  },
  {
    quote: "Every bit of advice from Niki has made me feel healthier.",
    body: "The gut cleanse was unreal — I felt so much better afterwards, and losing a few kilos was a great side note.",
    name: "Helen.",
    role: "Gut Cleanse Client",
  },
  {
    quote: "A Ctrl-Alt-Del for body, mind and soul.",
    body: "Mentally, the programme took me to my edge — removing caffeine and sugar was both tough AND so worth doing. I felt sharper, more focused, positive and more resilient.",
    name: "Claire D.",
    role: "Heavy Metal Cleanse",
  },
];

const FAQ_ITEMS = [
  {
    q: "What actually happens in a consultation?",
    a: "Niki spends the full 60 minutes on you: your health history, symptoms, diet, lifestyle and goals. She works back to the root causes and sends you a written personalised plan within a few days.",
    trackLabel: "What happens in a consultation?",
  },
  {
    q: "Is this compatible with my GP or specialist?",
    a: "Yes. Naturopathic nutrition is complementary, not a replacement for medical care. Niki works alongside conventional treatment and will always refer you on if something needs medical investigation.",
    trackLabel: "Compatible with GP?",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes, payment plans are available for the Farleigh Method and consultation packages. Get in touch after booking or mention it on your discovery call.",
    trackLabel: "Payment plans?",
  },
  {
    q: "How do I know which option is right for me?",
    a: "The 3-question quiz above is the fastest way to find out. Or book a free discovery call: a 15-minute chat where Niki will tell you exactly what she'd recommend for your situation.",
    trackLabel: "Which option is right?",
  },
];

function computeResult(answers: number[][]): Path {
  const totals: Record<Path, number> = { discovery: 0, consultation: 0, farleigh: 0, bodhi: 0 };
  answers.forEach((selected, qIdx) => {
    selected.forEach((optIdx) => {
      const scores = QUESTIONS[qIdx].options[optIdx].scores as Partial<Record<Path, number>>;
      (Object.keys(scores) as Path[]).forEach((p) => { totals[p] += scores[p] ?? 0; });
    });
  });
  return (Object.keys(totals) as Path[]).reduce((best, p) =>
    totals[p as Path] > totals[best] ? (p as Path) : best
  ) as Path;
}

export default function WorkWithMeClient() {
  const [quizStep, setQuizStep] = useState<"idle" | "quiz" | "result">("idle");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<number[][]>(Array(QUESTIONS.length).fill([]));
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<Path | null>(null);

  function startQuiz() {
    track("quiz_start", { label: "start", component: "WorkWithMePage" }, { event: "quiz_start", label: "start", component: "WorkWithMePage" });
    setQuizStep("quiz");
    setCurrent(0);
    setAnswers(Array(QUESTIONS.length).fill([]));
    setSelected(null);
    setResult(null);
    setTimeout(() => document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  function handleNext() {
    if (selected === null) return;
    const newAnswers = answers.map((a, i) => (i === current ? [selected] : a));
    setAnswers(newAnswers);
    track(
      "quiz_answer",
      { label: `Q${current + 1}:A${selected}`, component: "WorkWithMePage" },
      { event: "quiz_answer", label: `Q${current + 1}:A${selected}`, component: "WorkWithMePage", meta: { question: current + 1, answer: selected } }
    );
    if (current < QUESTIONS.length - 1) {
      setCurrent(current + 1);
      setSelected(null);
    } else {
      const path = computeResult(newAnswers);
      setResult(path);
      setQuizStep("result");
      track("quiz_complete", { label: path, component: "WorkWithMePage" }, { event: "quiz_complete", label: path, component: "WorkWithMePage", meta: { result: path } });
    }
  }

  function handleBack() {
    if (current === 0) { setQuizStep("idle"); return; }
    setCurrent(c => c - 1);
    setSelected(answers[current - 1]?.[0] ?? null);
  }

  const progress = Math.round(((current + 1) / QUESTIONS.length) * 100);

  /* Shared editorial helpers ------------------------------------------------ */
  const eyebrow =
    "flex items-center gap-3 text-[0.75rem] font-medium tracking-[0.1em] uppercase text-[var(--text-heading)]";
  const eyebrowCentered =
    "block text-[0.75rem] font-medium tracking-[0.12em] uppercase mb-3 text-[var(--text-heading)]";
  const outlinePill =
    "inline-flex items-center justify-center rounded-full px-8 py-4 border border-[var(--text-on-light)] text-[var(--text-on-light)] font-medium text-[0.95rem] transition-all hover:-translate-y-0.5 hover:text-[var(--text-brand)] hover:border-[var(--text-brand)]";

  return (
    <main className="bg-[var(--bg-page)] overflow-x-hidden">

      {/* ── HERO — light section (cream) ──────────────────────────────── */}
      <header className="relative max-w-[1200px] mx-auto px-6 pt-14 md:pt-20 pb-16 grid md:grid-cols-2 gap-12 items-center">
        <div className="order-2 md:order-1">
          <p className={`${eyebrow} mb-6`}>
            <span className="block w-7 h-px bg-[var(--accent-sage)]" />
            Niki Charnock · Southport, Manchester &amp; Online
          </p>

          {/*
            The h1 used to be "Still searching for why you don't feel well?" — good
            hook, but nothing on the page then named what the page actually is, and
            the <title> didn't either. The descriptive line is the h1 now; the hook
            keeps its visual weight directly beneath it.
          */}
          <h1 className="font-serif text-[clamp(2.4rem,5vw,3.8rem)] font-light leading-[1.12] text-[var(--text-on-light)] mb-4">
            Naturopathic Nutrition<br />
            Consultations
          </h1>

          <p className="font-serif text-[clamp(1.35rem,2.8vw,1.9rem)] font-light leading-[1.25] text-[var(--text-on-light)] mb-6">
            Still searching for why you don&apos;t feel well?
          </p>

          <p className="text-[1.05rem] text-[var(--text-on-light-muted)] leading-[1.75] mb-8 max-w-[440px]">
            Niki Charnock works back from the symptoms to the root cause. Answer three
            quick questions and we&apos;ll point you to the right next step.
          </p>

          <div className="flex flex-col items-start gap-3">
            <button onClick={startQuiz} className={outlinePill}>
              Find my path in 60 seconds →
            </button>
            <p className="text-[0.8rem] text-[var(--text-on-light-faint)] italic">
              60 seconds · no email required.
            </p>
            <Link
              href="/workwithme/discoverycall"
              onClick={() => track("click", { label: "Hero - Book Free Call", component: "WorkWithMePage" }, { event: "click", label: "Hero - Book Free Call", component: "WorkWithMePage" })}
              className="text-[var(--color-link)] border-b border-[var(--color-link)]/40 pb-0.5 text-[0.9rem] hover:text-[var(--text-brand)] hover:border-[var(--text-brand)] transition-colors mt-3"
            >
              Prefer to talk first? Book a free discovery call →
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-8 text-[0.78rem] text-[var(--text-on-light-faint)]">
            <span>100% personalised</span><span aria-hidden>·</span>
            <span>Root-cause focused</span><span aria-hidden>·</span>
            <span>Free discovery call</span>
            {/* The title now claims "Prices", so a price has to be visible above the
                fold, not only in the services section further down. */}
            {feeLabel("consultation") && (
              <>
                <span aria-hidden>·</span>
                <a href="#services" className="hover:text-[var(--text-brand)] transition-colors">
                  Consultations from {feeLabel("consultation")}
                </a>
              </>
            )}
          </div>
        </div>

        <div className="order-1 md:order-2 relative">
          <div className="relative overflow-hidden aspect-[4/5] rounded-[60%_40%_55%_45%/50%_50%_50%_50%] bg-[var(--bg-surface)]">
            <Image
              src="/images/Niki_Charnock.webp"
              alt="Niki Charnock — Naturopathic Nutritionist"
              fill
              className="object-cover object-top"
              priority
            />
          </div>

          <div className="absolute bottom-8 -left-4 md:-left-10 bg-[var(--bg-surface)] rounded-2xl px-5 py-4 shadow-lg border border-[var(--border-on-light)]">
            <p className="font-serif text-[1.05rem] font-semibold text-[var(--text-on-light)]">
              Niki Charnock
            </p>
            <p className="text-[0.72rem] text-[var(--text-on-light-muted)] mt-0.5 leading-snug">
              Naturopathic Nutritionist<br />
              Root-cause · Functional Testing
            </p>
          </div>
        </div>
      </header>

      {/* ── INLINE QUIZ — soft mist band ──────────────────────────────── */}
      <section id="quiz" className="bg-[var(--bg-soft)] border-y border-[var(--border-on-light)] scroll-mt-24">
        <div className="mx-auto max-w-2xl px-6 py-16 md:py-20 pb-28 md:pb-20">

          {quizStep === "idle" && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className={eyebrowCentered}>Not sure where to start?</span>
              <h2 className="font-serif text-[clamp(1.7rem,3.5vw,2.4rem)] font-light text-[var(--text-on-light)] mb-3">
                Find your perfect fit in 3 questions
              </h2>
              <p className="text-[var(--text-on-light-muted)] leading-[1.75] mb-8">Takes 60 seconds. No email required.</p>
              <button onClick={startQuiz} className={outlinePill}>
                Start the quiz →
              </button>
            </motion.div>
          )}

          {quizStep === "quiz" && (
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.22 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="flex-1 h-1.5 bg-[var(--bg-surface-dk)] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[var(--accent-sage)] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <span className="text-xs text-[var(--text-on-light-faint)] shrink-0">{current + 1} / {QUESTIONS.length}</span>
                </div>

                <h2 className="font-serif text-[clamp(1.4rem,3vw,2rem)] font-light text-[var(--text-on-light)] mb-6">
                  {QUESTIONS[current].text}
                </h2>

                <div className="space-y-3 mb-8">
                  {QUESTIONS[current].options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelected(idx)}
                      className={`w-full text-left rounded-2xl border px-5 py-4 text-[0.95rem] transition-all ${
                        selected === idx
                          ? "border-[var(--text-brand)] bg-[var(--bg-card)] text-[var(--text-on-light)] shadow-sm"
                          : "border-[var(--border-on-light)] bg-[var(--bg-card)] text-[var(--text-on-light-muted)] hover:border-[var(--text-brand)]"
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold mr-3 flex-shrink-0 transition-colors ${
                        selected === idx ? "bg-[var(--text-brand)] text-[var(--text-on-dark)]" : "bg-[var(--bg-surface-dk)] text-[var(--text-on-light-muted)]"
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <button onClick={handleBack} className="text-sm text-[var(--text-on-light-faint)] hover:text-[var(--text-on-light-muted)] transition-colors min-h-[44px] px-2">
                    ← Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={selected === null}
                    className={`rounded-full font-medium px-8 py-3 text-[0.9rem] border transition-all ${
                      selected !== null
                        ? "border-[var(--text-on-light)] text-[var(--text-on-light)] hover:text-[var(--text-brand)] hover:border-[var(--text-brand)] hover:-translate-y-0.5"
                        : "border-[var(--border-on-light)] text-[var(--text-on-light-faint)] cursor-not-allowed"
                    }`}
                  >
                    {current < QUESTIONS.length - 1 ? "Next →" : "See my result →"}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {quizStep === "result" && result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--bg-card)] border border-[var(--accent-sage)] text-3xl mb-4"
                >
                  {RESULTS[result].emoji}
                </motion.div>
                <span className={eyebrowCentered}>Your recommendation</span>
                <h2 className="font-serif text-[clamp(1.7rem,3.5vw,2.4rem)] font-light text-[var(--text-on-light)] mb-2">
                  {RESULTS[result].title}
                </h2>
                <p className="text-[var(--text-on-light-muted)] leading-[1.7] max-w-sm mx-auto">{RESULTS[result].tagline}</p>
              </div>

              <div className="rounded-2xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[0.72rem] font-medium text-[var(--text-on-light-faint)] uppercase tracking-[0.1em]">What&apos;s included</p>
                  <span className="text-[0.95rem] font-medium text-[var(--text-brand)]">{RESULTS[result].charge}</span>
                </div>
                <ul className="space-y-2.5">
                  {RESULTS[result].highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-[0.92rem] text-[var(--text-on-light-muted)]">
                      <span className="block w-[18px] h-px flex-shrink-0 bg-[var(--accent-sage)]" /> {h}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                href={RESULTS[result].href}
                onClick={() => track("click", { label: `Quiz Result CTA - ${result}`, component: "WorkWithMePage" }, { event: "click", label: `Quiz Result CTA - ${result}`, component: "WorkWithMePage", meta: { result } })}
                className="flex items-center justify-center gap-2 w-full rounded-full py-4 mb-4 border border-[var(--text-on-light)] text-[var(--text-on-light)] font-medium transition-all hover:-translate-y-0.5 hover:text-[var(--text-brand)] hover:border-[var(--text-brand)]"
              >
                {RESULTS[result].cta} →
              </Link>

              <button
                onClick={startQuiz}
                className="w-full text-center text-[0.8rem] text-[var(--text-on-light-faint)] hover:text-[var(--text-on-light-muted)] underline underline-offset-2 py-2"
              >
                Retake the quiz
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── GOALS — light section (cream) ─────────────────────────────── */}
      <section className="bg-[var(--bg-page)] py-20 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] font-light text-[var(--text-on-light)]">What are you working on?</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {GOALS.map(({ icon, label, href }) => (
              <Link
                key={label}
                href={href}
                onClick={() => track("click", { label: `Goal card: ${label}`, component: "WorkWithMePage" }, { event: "click", label: `Goal card: ${label}`, component: "WorkWithMePage" })}
                className="flex flex-col items-center gap-2 rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-5 text-center transition-all hover:border-[var(--text-brand)] hover:-translate-y-0.5"
              >
                <span className="text-2xl" aria-hidden>{icon}</span>
                <span className="text-[0.85rem] text-[var(--text-on-light)]">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CHOOSE YOUR PATH — surface band ───────────────────────────── */}
      <section className="bg-[var(--bg-surface)] py-16 md:py-20 px-6 border-y border-[var(--border-on-light)]">
        <div className="max-w-[980px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] font-light text-[var(--text-on-light)]">Choose your path</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { arrow: "Not sure where to start?", rec: "Free Discovery Call", href: "/workwithme/discoverycall", label: "discovery" },
              { arrow: "Want structured results fast?", rec: "The Farleigh Method", href: "/workwithme/farleighMethod", label: "farleigh" },
              { arrow: "Returning or follow-up client?", rec: "Book direct below", href: "#services", label: "returning" },
            ].map(({ arrow, rec, href, label }) => (
              <Link
                key={label}
                href={href}
                onClick={() => track("click", { label: `Choose path: ${label}`, component: "WorkWithMePage" }, { event: "click", label: `Choose path: ${label}`, component: "WorkWithMePage" })}
                className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-6 transition-all hover:border-[var(--text-brand)] hover:-translate-y-0.5"
              >
                <p className="text-[0.8rem] text-[var(--text-on-light-muted)] mb-2">{arrow}</p>
                <p className="font-serif text-[1.15rem] font-light text-[var(--text-brand)]">{rec} →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES — light section (cream) ──────────────────────────── */}
      <section id="services" className="bg-[var(--bg-page)] py-20 px-6 scroll-mt-24">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-[clamp(2rem,4vw,2.8rem)] font-light text-[var(--text-on-light)]">Choose your level of support</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(Object.entries(RESULTS) as [Path, (typeof RESULTS)[Path]][]).map(([key, r]) => (
              <div
                key={key}
                className={`relative rounded-2xl bg-[var(--bg-card)] p-7 flex flex-col gap-4 border overflow-hidden ${r.badge ? "border-[var(--accent-sage)]" : "border-[var(--border-on-light)]"}`}
              >
                {r.badge && <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent-sage)]" />}
                {r.badge && (
                  <span className="self-start text-[0.7rem] font-medium tracking-[0.1em] uppercase text-[var(--text-heading)]">
                    {r.badge}
                  </span>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-2xl" aria-hidden>{r.emoji}</span>
                    <h3 className="mt-2 font-serif text-[1.3rem] font-light text-[var(--text-on-light)]">{r.title}</h3>
                    <p className="text-[0.9rem] text-[var(--text-on-light-muted)] mt-1 leading-[1.6]">{r.tagline}</p>
                  </div>
                  <span className="text-[0.95rem] font-medium text-[var(--text-brand)] shrink-0">{r.charge}</span>
                </div>
                <ul className="space-y-2">
                  {r.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-[0.88rem] text-[var(--text-on-light-muted)]">
                      <span className="block w-[18px] h-px flex-shrink-0 bg-[var(--accent-sage)]" /> {h}
                    </li>
                  ))}
                </ul>
                <Link
                  href={r.href}
                  onClick={() => track("click", { label: `Service card CTA - ${key}`, component: "WorkWithMePage" }, { event: "click", label: `Service card CTA - ${key}`, component: "WorkWithMePage" })}
                  className="mt-auto inline-flex items-center justify-center rounded-full border border-[var(--text-brand)] text-[var(--text-brand)] text-[0.88rem] font-medium px-6 py-3 transition-all hover:bg-[var(--text-brand)] hover:text-[var(--text-on-dark)]"
                >
                  {r.cta} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS — soft mist section ──────────────────────────── */}
      <section className="bg-[var(--bg-soft)] py-20 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <span className={eyebrowCentered}>Client stories</span>
            <h2 className="font-serif text-[clamp(2rem,4vw,3rem)] font-light text-[var(--text-on-light)]">What clients say</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-7">
                <p className="text-[0.75rem] tracking-[2px] mb-3 text-[var(--accent)]" aria-label="5 stars" role="img">★★★★★</p>
                <blockquote className="font-serif text-[1.1rem] italic leading-snug text-[var(--text-on-light)] mb-4">&ldquo;{t.quote}&rdquo;</blockquote>
                <p className="text-[0.88rem] leading-[1.7] text-[var(--text-on-light-muted)] mb-5">{t.body}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-[1rem] font-semibold flex-shrink-0 bg-[var(--mist)] text-[var(--sage-dk)]">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[0.85rem] font-medium text-[var(--text-on-light)]">{t.name}</p>
                    <p className="text-[0.75rem] mt-0.5 text-[var(--text-on-light-faint)]">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ — dark section (near-black ink) ───────────────────────── */}
      <section id="faq" className="bg-[var(--ink)] py-20 px-6">
        <div className="max-w-[740px] mx-auto">
          <h2 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] font-light text-[var(--text-on-dark)] mb-10">
            Common questions
          </h2>
          <FaqAccordion faqs={FAQ_ITEMS} />
        </div>
      </section>

      {/* ── STICKY MOBILE CTA ─────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/80">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-[0.85rem] text-[var(--text-on-light)]">
            {quizStep === "quiz" ? "Skip the quiz, book direct." : "Not sure? Start free."}
          </span>
          <Link
            href="/workwithme/discoverycall"
            onClick={() => track("click", { label: `Sticky Mobile CTA - Book Free Call${quizStep === "quiz" ? " (skip quiz)" : ""}`, component: "WorkWithMePage" }, { event: "click", label: `Sticky Mobile CTA - Book Free Call${quizStep === "quiz" ? " (skip quiz)" : ""}`, component: "WorkWithMePage" })}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--text-brand)] text-[var(--text-brand)] font-medium text-[0.85rem] px-5 py-2.5 transition-all hover:bg-[var(--text-brand)] hover:text-[var(--text-on-dark)]"
          >
            Free discovery call
          </Link>
        </div>
      </div>

      {/* ── FINAL CTA — dark section (near-black ink) ─────────────────── */}
      <section className="bg-[var(--ink)] py-24 pb-28 md:pb-24 px-6 relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-1/2 -right-[10%] w-[600px] h-[600px] rounded-full opacity-10 bg-[var(--accent)]"
        />
        <div className="max-w-[740px] mx-auto text-center relative">
          <h2 className="font-serif text-[clamp(2rem,4.5vw,3.4rem)] font-light leading-[1.2] mb-5 text-[var(--cream)]">
            Ready to feel like<br />yourself again?
          </h2>

          <p className="max-w-[500px] mx-auto mb-10 text-[var(--sand)] leading-[1.75]">
            Start with a free 15-minute discovery call and get some clarity on your next step.
          </p>

          <Link
            href="/workwithme/discoverycall"
            onClick={() => track("click", { label: "Footer CTA - Book Free Call", component: "WorkWithMePage" }, { event: "click", label: "Footer CTA - Book Free Call", component: "WorkWithMePage" })}
            className="inline-flex items-center justify-center rounded-full px-10 py-4 font-medium transition-all hover:-translate-y-0.5 bg-[var(--cream)] text-[var(--ink)] hover:bg-[var(--cream-dk)]"
          >
            Book your free discovery call →
          </Link>

          <p className="mt-6 text-[0.82rem] italic text-[var(--text-on-dark-faint)]">
            Face to face in Manchester (Wed) or Southport (Mon &amp; Thu), or online.
          </p>
        </div>
      </section>

    </main>
  );
}
